import { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  PieChart, Pie, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList,
} from "recharts";
import Preloader from "../../shared/PreLoaders";
import { Authorization } from "../../Context/Authorization";
import { AuthContext } from "../../Context/AuthContext";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const TransactionProfile = () => {
  const [ranges, setRanges] = useState([]);
  const [selectedRange, setSelectedRange] = useState("");
  const [vfcData, setVfcData] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const Api = process.env.REACT_APP_API;
  const { selectedJfmcType } = useContext(AuthContext);

  useEffect(() => {
    const fetchRanges = async () => {
      setLoading(true);
      try {
        const Config = Authorization();
        const response = await axios.get(`${Api}/api/ranges`, Config);
        if (Array.isArray(response.data)) setRanges(response.data);
      } catch (err) {
        console.error("Error fetching data", err);
      }
      setLoading(false);
    };
    fetchRanges();
  }, [Api]);

  useEffect(() => {
    if (!selectedRange || !selectedJfmcType) { setVfcData([]); return; }
    const fetchVfcsByRange = async () => {
      setLoading(true);
      try {
        const Config = Authorization();
        const response = await axios.post(`${Api}/api/vfcs/vfcs-by-range`, { rangeId: selectedRange, jfmcTypeId: selectedJfmcType._id }, Config);
        setVfcData(response.data.vfcs || []);
      } catch (error) {
        console.error("Error fetching VFCs", error);
        setVfcData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVfcsByRange();
  }, [selectedRange, selectedJfmcType, Api]);

  const handleViewClick = async () => {
    if (!selectedRange || !selectedVfc) return;
    setLoading(true);
    try {
      const Config = Authorization();
      const response = await axios.post(`${Api}/api/transactions/getTransactionByVFC`, { vfcId: selectedVfc }, Config);
      setTransactions(response.data || []);
    } catch (error) {
      console.error("Error fetching transactions", error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRangeChange = (event) => { setSelectedRange(event.target.value); setSelectedVfc(""); setTransactions([]); };
  const handleVfcChange = (event) => { setSelectedVfc(event.target.value); setTransactions([]); };

  const selectedTransaction = transactions.find((txn) => txn.vfcId && txn.vfcId._id === selectedVfc);
  const selectedVfcInfo = selectedTransaction ? selectedTransaction.vfcId : vfcData.find((vfc) => vfc._id === selectedVfc);

  const receiptData = transactions.filter((txn) => txn.type === "R").map((txn) => ({ name: txn.transactionDate, value: txn.amount }));
  const paymentData = transactions.filter((txn) => txn.type === "P").map((txn) => ({ name: txn.transactionDate, value: txn.amount }));
  const transactionTrendData = transactions.map((txn) => ({ date: new Date(txn.transactionDate).toLocaleDateString(), amount: txn.amount }));

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Transaction Profile</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Transactions</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Transaction Profile</span>
        </div>
      </div>

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-filter-variant" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Select VFC</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Range</label>
                <select value={selectedRange} onChange={handleRangeChange} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                  <option value="">Select Range</option>
                  {ranges.map((range) => <option key={range._id} value={range._id}>{range.rangeName}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>VFC Name</label>
                <select value={selectedVfc} onChange={handleVfcChange} disabled={!selectedRange} style={{ ...inputStyle, opacity: !selectedRange ? 0.6 : 1 }}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                  <option value="">Select VFC</option>
                  {vfcData.map((vfc) => <option key={vfc._id} value={vfc._id}>{vfc.vfcName}</option>)}
                </select>
              </div>
            </div>
            <button onClick={handleViewClick}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
              <i className="mdi mdi-file-chart" /> View
            </button>
          </div>

          {transactions.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 20 }}>
              {/* Left column */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* VFC Details Card */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
                    <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                      <i className="mdi mdi-office-building" />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>VFC Transaction Details</span>
                  </div>
                  <div style={{ padding: "16px 20px" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1A202C", marginBottom: 12 }}>
                      {vfcData.find((v) => v._id === selectedVfc)?.vfcName || "VFC Account"}
                    </div>
                    {selectedVfcInfo && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                        {[
                          ["Branch", selectedVfcInfo.branch],
                          ["Account Number", selectedVfcInfo.accountNumber],
                          ["IFSC Code", selectedVfcInfo.ifscCode],
                          ["Location", selectedVfcInfo.location],
                          ["Address", selectedVfcInfo.address],
                          ["Pin Code", selectedVfcInfo.pinCode],
                          ["Date of Establishment", selectedVfcInfo.dateOfEstablishment ? new Date(selectedVfcInfo.dateOfEstablishment).toLocaleDateString() : null],
                        ].map(([label, val]) => val ? (
                          <div key={label} style={{ display: "flex", gap: 8 }}>
                            <span style={{ fontWeight: 600, color: "#4A5568", minWidth: 130 }}>{label}:</span>
                            <span style={{ color: "#718096" }}>{val}</span>
                          </div>
                        ) : null)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Cash Book Card */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
                    <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                      <i className="mdi mdi-book-open-variant" />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>Cash Book Transactions</span>
                    <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 50 }}>{transactions.length}</span>
                  </div>
                  <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                    {transactions.map((txn) => (
                      <div key={txn._id} style={{ padding: "10px 14px", background: "#F7FAFC", borderRadius: 8, border: "1px solid #EDF2F7", fontSize: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ color: "#718096" }}>{new Date(txn.transactionDate).toLocaleDateString()}</span>
                          <span style={{ fontWeight: 700, color: "#1B5E20" }}>₹{txn.amount}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#4A5568", fontWeight: 600 }}>{txn.voucherType?.voucherName}</span>
                          <span style={{ color: "#718096" }}>{txn.partyDetails}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right column — Charts */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Pie Charts */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
                    <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                      <i className="mdi mdi-chart-pie" />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Receipts and Payments</span>
                  </div>
                  <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={receiptData} cx="50%" cy="50%" outerRadius={80} fill="#82ca9d" dataKey="value">
                            {receiptData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                            <LabelList dataKey="value" position="outside" formatter={(v) => v.toLocaleString("en-IN")} style={{ fontSize: 12, fontWeight: "bold" }} />
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ textAlign: "center", fontSize: 13, fontWeight: 700, color: "#2D3748", marginTop: 4 }}>Receipts</div>
                    </div>
                    <div>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={paymentData} cx="50%" cy="50%" outerRadius={80} fill="#8884d8" dataKey="value">
                            {paymentData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                            <LabelList dataKey="value" position="outside" formatter={(v) => v.toLocaleString("en-IN")} style={{ fontSize: 12, fontWeight: "bold" }} />
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ textAlign: "center", fontSize: 13, fontWeight: 700, color: "#2D3748", marginTop: 4 }}>Payments</div>
                    </div>
                  </div>
                </div>

                {/* Line Chart */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
                    <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                      <i className="mdi mdi-chart-line" />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Transaction Trends</span>
                  </div>
                  <div style={{ padding: "16px 20px" }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={transactionTrendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="amount" stroke="#1B5E20" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TransactionProfile;
